import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AssetScanner, { canScan } from './AssetScanner';
import axios from 'axios';
import { deviceName, LIFECYCLE_LABEL, holderLabel, tagText } from '../lib/assets';
import TagChip from './asset/TagChip';
import { contractorAuth } from '../lib/contractorAuth';
import './Assets.css';

/**
 * Which device is this ticket about?
 *
 * Grouped the way somebody actually looks for one, in this order:
 *
 *   My devices                 what is assigned to the requester
 *   Shared equipment near me   printers and screens in their building,
 *                              nearest first
 *   Someone else's device      a search by tag, serial, model or holder -
 *                              for the laptop that changed hands before the
 *                              register did
 *
 * and "No particular device" is always a choice, because most tickets are
 * not about a device and none of this is mandatory.
 *
 * WHOSE devices: the requester's, which is not always the person at the
 * keyboard. An administrator raising a ticket for Rita sees Rita's laptop,
 * and under "View as" the server already answers as the person being viewed.
 *
 * A CONTRACTOR'S (26 October: a request may name a device, from the standard
 * form). `contractor` is the portal's contractor - { id, viewing } from
 * lib/contractorAuth's portalContractor() - and for them the groups are
 * different because what they may name is different (lib/asset-link.js):
 *
 *   My devices                 what they hold - assigned to their linked
 *                              NCMB account, the My Equipment list
 *   Devices you look after     what their register grants reach
 *
 * no shared equipment, no "someone else's device". The server lists by that
 * rule (GET /assets/picker, /assets/search) and refuses anything outside it
 * on the POST, so nothing offered here is refused and nothing hidden can be
 * sent. Their own portal token signs the calls; an administrator looking
 * through View as holds none, so they name the contractor instead
 * (?contractor_id=, honoured for an administrator and nobody else).
 *
 * `initialId` preselects a device - "Report a problem" on a device's page
 * lands here with it chosen.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Option = ({ a, onPick, showHolder }) => (
  <li>
    <button type="button" className="as-option" onClick={() => onPick(a)}>
      <TagChip asset={a} />
      <span>{deviceName(a)}</span>
      <span className="as-sub">
        {[a.category_name,
          showHolder && a.holder_name ? `with ${holderLabel(a)}` : null,
          a.location_path,
          a.lifecycle_status === 'in_repair' ? LIFECYCLE_LABEL.in_repair : null]
          .filter(Boolean).join(' · ')}
      </span>
    </button>
  </li>
);

/*
 * `label` and `asFormGroup` draw the heading and the form row around the
 * picker - and only once it knows the register is on, so Create Ticket never
 * carries a "Device" heading over nothing.
 */
export default function AssetPicker({ value, onChange, requesterId, initialId = null,
  asFormGroup = false, label = null, contractor = null }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [found, setFound] = useState(null);
  const [error, setError] = useState('');
  const searchRef = useRef(null);
  const preselected = useRef(false);
  const [scanning, setScanning] = useState(false);

  // How every call here is signed and scoped: the contractor's token, or -
  // under View as - the contractor named; nothing at all for a member of
  // staff, whose call the app's interceptor signs as before.
  const contractorId = contractor?.id || null;
  const viewing = Boolean(contractor?.viewing);
  const config = useMemo(() => {
    if (!contractorId) return {};
    if (viewing) return { params: { contractor_id: contractorId } };
    return contractorAuth() || {};
  }, [contractorId, viewing]);
  const withParams = useCallback((params) => ({
    ...config, params: { ...(config.params || {}), ...params }
  }), [config]);

  useEffect(() => {
    let live = true;
    const params = requesterId && !contractorId ? { requester: requesterId } : {};
    axios.get(`${API_BASE}/assets/picker`, withParams(params))
      .then((r) => { if (live) setData(r.data); })
      .catch(() => { if (live) setData({ enabled: false, mine: [], shared: [] }); });
    return () => { live = false; };
  }, [requesterId, contractorId, withParams]);

  // A preselected device: from the lists if it is there, otherwise asked for
  // by id - the device page that sent us here is one this person can read.
  useEffect(() => {
    if (!data?.enabled || !initialId || preselected.current) return;
    preselected.current = true;
    const known = [...(data.mine || []), ...(data.shared || []), ...(data.granted || [])]
      .find((a) => a.id === initialId);
    if (known) { onChange(known); return; }
    axios.get(`${API_BASE}/assets/${initialId}`, config)
      .then((r) => {
        const a = r.data?.asset || r.data?.card;
        if (a) onChange(a);
      })
      .catch(() => {});
  }, [data, initialId, onChange, config]);

  // Search as they type, after a pause: one request per thought, not per key.
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) { setFound(null); return undefined; }
    const t = setTimeout(() => {
      const params = { q: term, ...(requesterId && !contractorId ? { requester: requesterId } : {}) };
      axios.get(`${API_BASE}/assets/search`, withParams(params))
        .then((res) => { setFound(res.data || []); setError(''); })
        .catch(() => setError('Could not search the register just now.'));
    }, 250);
    return () => clearTimeout(t);
  }, [query, requesterId, contractorId, withParams]);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  /*
   * A label was read. The same search the box uses decides whether this
   * person may choose it, so scanning can never offer a device that typing
   * its tag would not.
   */
  const onTag = useCallback(async (tag) => {
    setScanning(false);
    const params = { q: tag, ...(requesterId && !contractorId ? { requester: requesterId } : {}) };
    try {
      const res = await axios.get(`${API_BASE}/assets/search`, withParams(params));
      const exact = (res.data || []).find((a) => (a.asset_tag || "").toLowerCase() === tag.toLowerCase());
      if (exact) {
        onChange(exact);
        setOpen(false);
        setQuery('');
        setFound(null);
        return;
      }
      setQuery(tag);
      setError(`${tag} is not a device you can choose here.`);
    } catch {
      setError('Could not look that label up just now.');
    }
  }, [requesterId, contractorId, withParams, onChange]);

  if (!data || !data.enabled) return null;
  const forContractor = data.who === 'contractor';
  const frame = (inner) => (asFormGroup || label ? (
    <div className={asFormGroup ? 'form-group' : undefined}>
      {label && (
        <span className="as-picker-label">
          {label} <span className="as-quiet">(optional)</span>
        </span>
      )}
      {inner}
      {asFormGroup && (
        <div className="help-text">
          {forContractor
            ? 'The device this is about, if it is about one — what you hold or look after.'
            : 'The laptop, screen or printer this is about, if it is about one.'}
        </div>
      )}
    </div>
  ) : inner);

  const pick = (a) => {
    onChange(a);
    setOpen(false);
    setQuery('');
    setFound(null);
  };

  if (value && !open) {
    return frame(
      <div className="as-picker" data-asset-picker="chosen">
        <div className="as-picker-chosen">
          <TagChip asset={value} />
          <span>{deviceName(value)}</span>
          {value.holder_name && <span className="as-quiet">with {holderLabel(value)}</span>}
          <span className="as-spacer" />
          <button type="button" className="btn btn-plain btn-small" onClick={() => setOpen(true)}>
            Change
          </button>
          <button type="button" className="btn btn-plain btn-small" onClick={() => onChange(null)}>
            No device
          </button>
        </div>
      </div>
    );
  }

  if (!open) {
    return frame(
      <div className="as-picker" data-asset-picker="closed">
        <button type="button" className="as-picker-open" onClick={() => setOpen(true)}
          aria-expanded="false">
          {forContractor
            ? 'No particular device — choose one if this is about something you hold or look after'
            : 'No particular device — choose one if this is about a laptop, screen or printer'}
        </button>
        {/* Straight to the camera: the device's label says which machine it
            is better than anybody can describe it. */}
        {canScan() && (
          <button type="button" className="btn btn-secondary btn-small as-picker-scan"
            onClick={() => { setError(''); setOpen(true); setScanning(true); }}>
            Scan the label
          </button>
        )}
      </div>
    );
  }

  const mine = data.mine || [];
  const shared = data.shared || [];
  const granted = data.granted || [];
  return frame(
    <div className="as-picker" data-asset-picker="open">
      <div className="as-picker-panel" role="group" aria-label="Choose a device">
        <input
          ref={searchRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={data.any_device
            ? 'Search by tag, serial number, model or whose it is'
            : forContractor
              ? 'Search the devices you hold or look after'
              : 'Search your devices and shared equipment'}
          aria-label="Search for a device"
        />
        {canScan() && !scanning && (
          <button type="button" className="btn btn-secondary btn-small" onClick={() => { setError(''); setScanning(true); }}>
            Scan a label
          </button>
        )}
        {scanning && <AssetScanner onTag={onTag} onCancel={() => setScanning(false)} />}
        {error && <p className="as-error" role="alert">{error}</p>}

        {found !== null ? (
          <>
            <p className="as-picker-group">
              {data.any_device ? 'Any device'
                : forContractor ? 'Devices you hold or look after' : 'Your devices and shared equipment'}
            </p>
            {found.length === 0
              ? <p className="as-quiet">Nothing in the register matches “{query.trim()}”.</p>
              : <ul className="as-options">{found.map((a) => <Option key={a.id} a={a} onPick={pick} showHolder />)}</ul>}
          </>
        ) : (
          <>
            <p className="as-picker-group">My devices</p>
            {mine.length === 0
              ? <p className="as-quiet">Nothing is assigned to you in the register.</p>
              : <ul className="as-options">{mine.map((a) => <Option key={a.id} a={a} onPick={pick} />)}</ul>}
            {granted.length > 0 && (
              <>
                <p className="as-picker-group">Devices you look after</p>
                <ul className="as-options">{granted.map((a) => <Option key={a.id} a={a} onPick={pick} showHolder />)}</ul>
              </>
            )}
            {shared.length > 0 && (
              <>
                <p className="as-picker-group">Shared equipment near me</p>
                <ul className="as-options">{shared.map((a) => <Option key={a.id} a={a} onPick={pick} />)}</ul>
              </>
            )}
            {data.any_device && (
              <p className="as-quiet">Someone else’s device? Search for it above.</p>
            )}
          </>
        )}
        <div className="as-actions">
          <button type="button" className="btn btn-secondary btn-small" onClick={() => pick(null)}>
            No particular device
          </button>
          {value && (
            <button type="button" className="btn btn-plain btn-small" onClick={() => setOpen(false)}>
              Keep {tagText(value)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
